/**
 * PipelineFilters.tsx — every board filter, in one panel, applied together.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY IT REPLACED THE FUNNEL DIALOG (2026-08-30, CTO, second pass). The first
 * attempt put the funnel behind a modal and left entry-path on the bar. Three
 * things were wrong with it, and all three were right:
 *
 *  1. THE BAR DID NOT LINE UP. Two labelled fields (label above input) sat
 *     beside two bare buttons, so nothing shared a baseline. Fixed by taking
 *     everything except Search off the bar — one labelled field and one button
 *     cannot disagree about their own height.
 *  2. ONE FILTER AT A TIME. Picking a stage replaced the last one, so "show me
 *     everything waiting on us" meant looking at four stages one after another.
 *     Selections are now CHECKBOXES over a DRAFT, applied together.
 *  3. THE APPLIED FILTER LOOKED LIKE THE CLEAR BUTTON. Two same-weight buttons
 *     side by side, one of which was state and the other an action. State is
 *     now a removable CHIP and nothing else on the bar is chip-shaped.
 *
 * ⚠️ THE DRAFT IS THE POINT, not a nicety. Applying on every checkbox press
 * would fire a server read per click — four reads to select four stages, three
 * of them describing a filter nobody asked for. The panel edits a local copy
 * and Apply commits it in one go, which is also what makes "Clear all" a single
 * decision rather than an undo sequence.
 *
 * Entry path moved in here as a multi-select too. It stays a CLIENT-side facet
 * (see PipelineTab) — it describes cards already on screen and needs no round
 * trip — but from the user's side it is one more thing to tick, and splitting
 * the controls by which side of the wire they run on would be an implementation
 * detail leaking into the UI.
 */
import { useState } from 'react';
import type { DealEntryPath, DealCounts, DealStage } from '../api/types';
import type { DropOffReasonKey } from '../journey/dropOffReasons';
import { DialogShell } from '../ui/ConfirmDialog';
/* The panel's furniture is the ESTATE's, not this board's (2026-09-05). Tick,
   Section and the footer were module-private here until the Patients directory
   needed the same panel; they now live in `ui/filters.tsx` so the two screens
   cannot drift into two dialects of one control. Only the FACETS below —
   stages, entry paths, test records — belong to the pipeline. */
import {
  FilterFooter,
  FilterSection,
  FilterTick,
  filterGridStyle,
  filterNoteStyle,
} from '../ui/filters';
import { FUNNEL_GROUPS, groupTotal, openShare } from './funnelGroups';
import { ENTRY_LABEL, STAGE_LABEL } from './stages';
import { DROP_OFF_REASON_KEYS, dropOffFilterLabel } from './dropOffReasonsStaff';

export interface PipelineFilterState {
  stages: readonly DealStage[];
  entryPaths: readonly DealEntryPath[];
  showTest: boolean;
  /**
   * Show cards that were MERGED INTO another deal (2026-09-06).
   *
   * ⚠️ A SEPARATE SWITCH FROM `showTest`, AND IT HAS TO STAY ONE. A test record
   * is a rehearsal that never happened; a merged duplicate is a REAL patient
   * who submitted twice and may have been charged twice. Staff hunting a
   * patient's missing second submission and staff auditing QA dry-runs are two
   * different jobs, and one shared "show hidden" switch would make each wade
   * through the other's rows — and would be one edit away from making the two
   * concepts share a filter, which is the collapse the money rule depends on
   * never happening.
   *
   * Until this existed the `closed_duplicate` lane rendered as an empty column
   * on every board: the server requires `?includeDuplicates=true` before it
   * will serve a merged row, and nothing ever sent it.
   */
  showDuplicates: boolean;
  /**
   * Keep the cards whose patient gave one of these reasons for not paying
   * (T-2, 2026-09-11). SERVER-side — `dropOffReasons` on both board reads — so
   * it narrows every lane, "View more" included, rather than the loaded page.
   * It matches anyone who gave the reason, including patients who went on to
   * pay; a stage filter narrows it to the still-unpaid.
   */
  dropOffReasons: readonly DropOffReasonKey[];
}

export const EMPTY_FILTERS: PipelineFilterState = {
  stages: [],
  entryPaths: [],
  showTest: false,
  showDuplicates: false,
  dropOffReasons: [],
};

/** How many distinct choices a state represents — the bar's badge count. */
export function activeFilterCount(f: PipelineFilterState): number {
  return (
    f.stages.length +
    f.entryPaths.length +
    (f.showTest ? 1 : 0) +
    (f.showDuplicates ? 1 : 0) +
    f.dropOffReasons.length
  );
}

const ENTRY_PATHS = Object.keys(ENTRY_LABEL) as DealEntryPath[];

export function PipelineFilters({
  counts,
  applied,
  onApply,
  onClose,
}: {
  /** Tenant-wide per-stage totals; null when that read failed. */
  counts: DealCounts | null;
  applied: PipelineFilterState;
  onApply: (next: PipelineFilterState) => void;
  onClose: () => void;
}) {
  // The DRAFT. Seeded from what is applied, so opening the panel shows the
  // current state and closing without applying changes nothing.
  const [draft, setDraft] = useState<PipelineFilterState>(applied);

  const toggleStage = (stage: DealStage) =>
    setDraft((d) => ({
      ...d,
      stages: d.stages.includes(stage) ? d.stages.filter((s) => s !== stage) : [...d.stages, stage],
    }));

  const toggleEntry = (path: DealEntryPath) =>
    setDraft((d) => ({
      ...d,
      entryPaths: d.entryPaths.includes(path)
        ? d.entryPaths.filter((p) => p !== path)
        : [...d.entryPaths, path],
    }));

  const toggleReason = (reason: DropOffReasonKey) =>
    setDraft((d) => ({
      ...d,
      dropOffReasons: d.dropOffReasons.includes(reason)
        ? d.dropOffReasons.filter((r) => r !== reason)
        : [...d.dropOffReasons, reason],
    }));

  return (
    <DialogShell title="Filters" onCancel={onClose} width="min(720px, 100%)">
      {counts === null ? (
        // Honest rather than zeroed: a failed counts read is not an empty
        // pipeline, and rendering "0" against every stage would say it was.
        <p style={filterNoteStyle}>
          Stage totals are unavailable right now — you can still filter, the numbers just aren&rsquo;t
          shown.
        </p>
      ) : null}

      <FilterSection label="Filter by entry path" hint="How the person reached us.">
        <div style={filterGridStyle}>
          {ENTRY_PATHS.map((path) => (
            <FilterTick
              key={path}
              label={ENTRY_LABEL[path]}
              checked={draft.entryPaths.includes(path)}
              onChange={() => toggleEntry(path)}
            />
          ))}
        </div>
      </FilterSection>

      {/* The funnel, grouped by WHO IS BLOCKED rather than by lifecycle —
          lifecycle would restate the board's own left-to-right order and add
          nothing. See funnelGroups.ts. */}
      {FUNNEL_GROUPS.map((group) => (
        <FilterSection
          key={group.key}
          label={group.label}
          hint={group.hint}
          total={counts ? groupTotal(group, counts) : undefined}
        >
          <div style={filterGridStyle}>
            {group.stages.map((stage) => (
              <FilterTick
                key={stage}
                label={STAGE_LABEL[stage]}
                count={counts ? (counts[stage] ?? 0) : undefined}
                // Closed stages get no bar: they are measured against the OPEN
                // pipeline, so a bar there would be meaningless or over 100%.
                share={group.key === 'closed' ? 0 : openShare(counts?.[stage] ?? 0, counts)}
                checked={draft.stages.includes(stage)}
                onChange={() => toggleStage(stage)}
              />
            ))}
          </div>
        </FilterSection>
      ))}

      {/* WHY THEY HAVEN'T PAID (T-2, 2026-09-11). The two clinical reasons
          name who acts on them in the tick itself, so an operator building a
          call list cannot pick "side effects" without being told it is a
          clinician's call. No counts here: the badge counts are per STAGE, and
          a per-reason number belongs to the Insights ranking. */}
      <FilterSection
        label="Why they haven’t paid"
        hint="What approved patients told us is holding them back. Matches everyone who gave the reason, including anyone who went on to pay."
      >
        <div style={filterGridStyle}>
          {DROP_OFF_REASON_KEYS.map((reason) => (
            <FilterTick
              key={reason}
              label={dropOffFilterLabel(reason)}
              checked={draft.dropOffReasons.includes(reason)}
              onChange={() => toggleReason(reason)}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection label="Other" hint="Reporting hygiene, not deal work.">
        <FilterTick
          label="Show test records"
          describedBy="filters-test-hint"
          checked={draft.showTest}
          onChange={() => setDraft((d) => ({ ...d, showTest: !d.showTest }))}
        />
        <p id="filters-test-hint" style={{ ...filterNoteStyle, margin: '4px 0 0 30px' }}>
          Marked dry-runs are off the board by default. The totals above always exclude them, so the
          board can show more than they count.
        </p>
        {/* ⚠️ ITS OWN TICK, DIRECTLY BENEATH THE TEST ONE AND DELIBERATELY NOT
            FOLDED INTO IT. The hint has to do real work here: an operator who
            reads "hidden cards" and ticks one switch will assume both kinds are
            bookkeeping noise, and a merged duplicate is a real patient with
            possibly-real money on them. So the copy says what a duplicate IS,
            not just that it is hidden. */}
        <FilterTick
          label="Show merged duplicates"
          describedBy="filters-duplicate-hint"
          checked={draft.showDuplicates}
          onChange={() => setDraft((d) => ({ ...d, showDuplicates: !d.showDuplicates }))}
        />
        <p id="filters-duplicate-hint" style={{ ...filterNoteStyle, margin: '4px 0 0 30px' }}>
          Cards merged into another deal — a real patient who submitted twice, not a dry run. They leave
          the working lanes when they are merged; turn this on to find one and release it.
        </p>
      </FilterSection>

      <FilterFooter onClear={() => setDraft(EMPTY_FILTERS)} onApply={() => onApply(draft)} />
    </DialogShell>
  );
}
